import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { api, formatApiError } from '@/App';
import { Plus, MagnifyingGlass, Buildings, Trash } from '@phosphor-icons/react';

const LEAD_STATUSES = ['Cold', 'Warm', 'Proposal Sent', 'Active', 'Inactive'];

const LEAD_COLORS = {
  'Cold': 'bg-blue-100 text-blue-700',
  'Warm': 'bg-yellow-100 text-yellow-700',
  'Proposal Sent': 'bg-purple-100 text-purple-700',
  'Active': 'bg-green-100 text-green-700',
  'Inactive': 'bg-gray-100 text-gray-700'
};

const AdminCompanies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [newCompany, setNewCompany] = useState({
    company_name: '',
    company_email: '',
    company_phone: '',
    physical_address: '',
    vat_number: '',
    lead_status: 'Cold',
    lead_source: '',
    account_type: 'business',
    id_number: '',
    residential_address: '',
  });

  useEffect(() => {
    fetchCompanies();
  }, [search, statusFilter]);

  const fetchCompanies = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter) params.set('lead_status', statusFilter);
      const { data } = await api.get(`/companies?${params}`);
      setCompanies(data);
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = Object.fromEntries(
        Object.entries(newCompany).filter(([, v]) => v !== '' && v !== null && v !== undefined)
      );
      await api.post('/companies', payload);
      toast.success('Company created');
      setIsDialogOpen(false);
      setNewCompany({
        company_name: '',
        company_email: '',
        company_phone: '',
        physical_address: '',
        vat_number: '',
        lead_status: 'Cold',
        lead_source: '',
        account_type: 'business',
        id_number: '',
        residential_address: '',
      });
      fetchCompanies();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/companies/${deleteTarget.company_id}`);
      toast.success(`${deleteTarget.company_name} deleted along with all related records`);
      setDeleteTarget(null);
      fetchCompanies();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AdminLayout title="Companies">
      <div className="space-y-6 fade-in">
        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="relative flex-1 min-w-[200px]">
                <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4B5563]" />
                <Input
                  placeholder="Search companies..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 rounded-none"
                  data-testid="search-input"
                />
              </div>

              <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-40 rounded-none" data-testid="status-filter">
                  <SelectValue placeholder="Lead Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {LEAD_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#0055FF] hover:bg-[#0044CC] rounded-none" data-testid="create-company-btn">
                    <Plus className="w-4 h-4 mr-2" /> Add Company
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px]">
                  <DialogHeader>
                    <DialogTitle>Add New Company</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreate} className="space-y-4 mt-4">
                    <div>
                      <Label className="text-sm font-semibold">Account Type *</Label>
                      <div className="grid grid-cols-2 gap-2 mt-1" data-testid="account-type-toggle">
                        <button
                          type="button"
                          onClick={() => setNewCompany({ ...newCompany, account_type: 'business' })}
                          className={`p-3 border-2 text-sm font-medium transition-colors ${newCompany.account_type === 'business' ? 'border-[#0055FF] bg-[#0055FF]/5 text-[#0055FF]' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                          data-testid="account-type-business"
                        >
                          Business
                        </button>
                        <button
                          type="button"
                          onClick={() => setNewCompany({ ...newCompany, account_type: 'personal' })}
                          className={`p-3 border-2 text-sm font-medium transition-colors ${newCompany.account_type === 'personal' ? 'border-[#0055FF] bg-[#0055FF]/5 text-[#0055FF]' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                          data-testid="account-type-personal"
                        >
                          Personal
                        </button>
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-semibold">
                        {newCompany.account_type === 'business' ? 'Company Name *' : 'Full Name *'}
                      </Label>
                      <Input
                        value={newCompany.company_name}
                        onChange={(e) => setNewCompany({ ...newCompany, company_name: e.target.value })}
                        required
                        className="rounded-none mt-1"
                        data-testid="company-name-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-semibold">Email</Label>
                        <Input
                          type="email"
                          value={newCompany.company_email}
                          onChange={(e) => setNewCompany({ ...newCompany, company_email: e.target.value })}
                          className="rounded-none mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold">Phone</Label>
                        <Input
                          value={newCompany.company_phone}
                          onChange={(e) => setNewCompany({ ...newCompany, company_phone: e.target.value })}
                          className="rounded-none mt-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-semibold">
                        {newCompany.account_type === 'personal' ? 'Residential Address' : 'Physical Address'}
                      </Label>
                      <Input
                        value={newCompany.account_type === 'personal' ? newCompany.residential_address : newCompany.physical_address}
                        onChange={(e) => setNewCompany(newCompany.account_type === 'personal'
                          ? { ...newCompany, residential_address: e.target.value }
                          : { ...newCompany, physical_address: e.target.value })}
                        className="rounded-none mt-1"
                      />
                    </div>
                    {newCompany.account_type === 'personal' && (
                      <div>
                        <Label className="text-sm font-semibold">ID Number</Label>
                        <Input
                          value={newCompany.id_number}
                          onChange={(e) => setNewCompany({ ...newCompany, id_number: e.target.value })}
                          placeholder="13-digit SA ID"
                          className="rounded-none mt-1 font-mono"
                          data-testid="company-id-number-input"
                        />
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-semibold">
                          {newCompany.account_type === 'personal' ? 'Tax Number' : 'VAT Number'}
                        </Label>
                        <Input
                          value={newCompany.vat_number}
                          onChange={(e) => setNewCompany({ ...newCompany, vat_number: e.target.value })}
                          className="rounded-none mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold">Lead Status</Label>
                        <Select value={newCompany.lead_status} onValueChange={(v) => setNewCompany({ ...newCompany, lead_status: v })}>
                          <SelectTrigger className="rounded-none mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {LEAD_STATUSES.map((s) => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-semibold">Lead Source</Label>
                      <Input
                        value={newCompany.lead_source}
                        onChange={(e) => setNewCompany({ ...newCompany, lead_source: e.target.value })}
                        placeholder="e.g., Website, Referral"
                        className="rounded-none mt-1"
                      />
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-none">
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-[#0055FF] hover:bg-[#0044CC] rounded-none" data-testid="submit-company-btn">
                        Create Company
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>

        {/* Companies Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? (
            <div className="col-span-full flex items-center justify-center h-32">
              <div className="w-8 h-8 border-4 border-[#0055FF] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : companies.length > 0 ? (
            companies.map((company) => (
              <div key={company.company_id} className="relative group">
                <Link to={`/admin/companies/${company.company_id}`} data-testid={`company-card-${company.company_id}`}>
                  <Card className="card-hover cursor-pointer h-full">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-12 h-12 bg-[#0055FF]/10 flex items-center justify-center">
                          <Buildings className="w-6 h-6 text-[#0055FF]" />
                        </div>
                        <Badge className={LEAD_COLORS[company.lead_status] || 'bg-gray-100'}>
                          {company.lead_status}
                        </Badge>
                      </div>
                      <h3 className="font-semibold text-[#0A192F] mb-1 pr-8">{company.company_name}</h3>
                      {company.parent_company_name && (
                        <p className="text-xs text-blue-700 mb-1" data-testid={`parent-of-${company.company_id}`}>↳ under {company.parent_company_name}</p>
                      )}
                      {company.child_count > 0 && (
                        <Badge variant="outline" className="text-xs rounded-none mb-1" data-testid={`child-count-${company.company_id}`}>
                          {company.child_count} sub-account{company.child_count !== 1 ? 's' : ''}
                        </Badge>
                      )}
                      {company.company_email && (
                        <p className="text-sm text-[#4B5563] truncate">{company.company_email}</p>
                      )}
                      {company.physical_address && (
                        <p className="text-sm text-[#4B5563] mt-2 line-clamp-2">{company.physical_address}</p>
                      )}
                    </CardContent>
                  </Card>
                </Link>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute bottom-2 right-2 h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-none opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDeleteTarget(company);
                  }}
                  data-testid={`delete-company-btn-${company.company_id}`}
                  aria-label={`Delete ${company.company_name}`}
                >
                  <Trash className="w-4 h-4" />
                </Button>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-12">
              <Buildings className="w-12 h-12 text-[#D1D5DB] mx-auto mb-4" />
              <p className="text-[#4B5563]">No companies found</p>
              <Button 
                onClick={() => setIsDialogOpen(true)} 
                className="mt-4 bg-[#0055FF] hover:bg-[#0044CC] rounded-none"
              >
                <Plus className="w-4 h-4 mr-2" /> Add First Company
              </Button>
            </div>
          )}
        </div>

        <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
          <AlertDialogContent data-testid="delete-company-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {deleteTarget?.company_name}?</AlertDialogTitle>
              <AlertDialogDescription>
                The company, its service cases, meters, contracts, contact links and documents will be permanently deleted.
                <br /><br />
                <strong>Financial records (quotations, invoices, signed contracts) are archived for 5 years</strong>
                to comply with POPIA &amp; the Tax Administration Act. They keep counting in Financials &amp; Reports.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting} data-testid="cancel-delete-company">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-company"
              >
                {deleting ? 'Deleting...' : 'Delete Permanently'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default AdminCompanies;
